/**
 * Cookie Consent Banner
 * WHY: GDPR compliance — inform users about cookies and get consent
 * v1.1 (2026-09-29, cc_tasks row 260): tighter on phones so it does not sit over the sign-in
 * buttons on /auth; same text and choices at every width.
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "mentionfox_cookie_consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (level: "all" | "essential") => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ level, date: new Date().toISOString() }));
    // T199: public/google-tag.js listens for this and updates Google Consent Mode to granted
    // on "Accept All" (never for a browser sending Global Privacy Control / Do Not Track).
    try { window.dispatchEvent(new Event("mf:cookie-consent")); } catch { /* next page load reads it */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-2 sm:p-4"
        >
          <div className="max-w-4xl mx-auto bg-auth-card border border-auth-border rounded-xl p-3 sm:p-6 shadow-2xl backdrop-blur-sm">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="hidden sm:flex w-10 h-10 rounded-lg bg-auth-accent/10 items-center justify-center shrink-0">
                <Cookie className="w-5 h-5 text-auth-accent" />
              </div>
              <div className="flex-1 space-y-2 sm:space-y-3">
                <h3 className="font-semibold text-sm sm:text-base text-auth-foreground">We value your privacy</h3>
                <p className="text-xs sm:text-sm text-auth-muted leading-snug sm:leading-relaxed">
                  We use essential cookies to make our platform work. With Accept All, we also use optional cookies to improve your experience and to measure our Google ads; with Essential Only, no advertising cookies are set.
                  Read our{" "}
                  <Link to="/privacy" className="text-auth-accent hover:underline">Privacy Policy</Link>
                  {" "}for more details.
                </p>
                <div className="flex flex-wrap gap-2 sm:gap-3 pt-1">
                  <Button
                    onClick={() => accept("all")}
                    className="bg-auth-accent hover:bg-auth-accent-hover text-white"
                    size="sm"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={() => accept("essential")}
                    variant="outline"
                    size="sm"
                    className="border-auth-border text-auth-foreground"
                  >
                    Essential Only
                  </Button>
                </div>
              </div>
              <button
                onClick={() => accept("essential")}
                className="text-auth-muted hover:text-auth-foreground transition-colors shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
